
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";
import { Settings as SettingsIcon, Crown, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { getRpgThemeClasses, getRpgTerminology } from "@/lib/utils";
import SettingsHeader from "@/components/settings/SettingsHeader";
import SettingsToggleItem from "@/components/settings/SettingsToggleItem";
import SettingsFooter from "@/components/settings/SettingsFooter";
import ErrorBoundary from "@/components/ErrorBoundary";

const Settings = () => {
  const [isSaving, setIsSaving] = useState(false);
  const {
    darkMode,
    autoProceed,
    enableLensFocus,
    enableFeedback,
    enableRpgMode,
    layoutDirection,
    toggleDarkMode,
    toggleAutoProceed,
    toggleLensFocus,
    toggleFeedback,
    toggleRpgMode,
    toggleLayoutDirection,
    saveSettings
  } = useSettings();

  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await saveSettings();
      toast({
        title: enableRpgMode ? "Preferences saved" : "Settings saved",
        description: enableRpgMode 
          ? "Your adventurer preferences have been recorded in the ancient tomes."
          : "Your preferences have been saved successfully."
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: enableRpgMode ? "Error saving preferences" : "Error saving settings",
        description: enableRpgMode 
          ? "The ancient scrolls resist your changes. Please try again."
          : "There was a problem saving your settings."
      });
      console.error("Settings save error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn(
      "min-h-screen flex flex-col items-center justify-center p-4 font-montserrat transition-colors duration-300",
      enableRpgMode ? (
        darkMode 
          ? "bg-gradient-to-br from-amber-900/40 via-purple-900/50 to-amber-800/40" 
          : "bg-gradient-to-br from-amber-100/80 via-orange-50 to-amber-200/60"
      ) : (
        darkMode 
          ? "bg-gradient-to-br from-[#1a0930] to-[#3F2E46] text-[#FBF3F1]" 
          : "bg-gradient-to-br from-[#FBF3F1] to-[#D896A4]/10"
      )
    )}>
      <ErrorBoundary fallback={<div>Settings header unavailable</div>}>
        <SettingsHeader enableRpgMode={enableRpgMode} darkMode={darkMode} />
      </ErrorBoundary>
      
      <Card className={cn(
        "w-full max-w-md shadow-lg border-2 backdrop-blur-sm p-6",
        enableRpgMode ? (
          darkMode 
            ? "bg-amber-900/80 border-amber-600/60 text-amber-50" 
            : "bg-amber-50/95 border-amber-400/60 text-amber-900"
        ) : (
          darkMode 
            ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
            : "bg-white/90 border-[#D896A4]/10"
        )
      )}>
        <div className="flex items-center mb-4">
          {enableRpgMode ? (
            <Crown className="h-6 w-6 mr-2 text-amber-600" />
          ) : (
            <SettingsIcon className={cn("h-6 w-6 mr-2", darkMode ? "text-[#00FF99]" : "text-[#6127A0]")} />
          )}
          <h2 className={cn(
            "text-xl font-semibold",
            enableRpgMode ? (
              darkMode ? "text-amber-100 font-bold" : "text-amber-900 font-bold"
            ) : (
              darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
            )
          )}>
            {enableRpgMode ? getRpgTerminology("Settings") : "App Settings"}
          </h2>
        </div>
        
        <div className="space-y-6">
          <SettingsToggleItem
            title={enableRpgMode ? "Shadow & Light Mode" : "Dark Mode"}
            description={enableRpgMode 
              ? "Toggle between the realm of shadows and light" 
              : "Switch to a darker color theme"
            }
            checked={darkMode}
            onCheckedChange={toggleDarkMode}
            enableRpgMode={enableRpgMode}
            darkMode={darkMode}
          />
          
          <SettingsToggleItem
            title={enableRpgMode ? "Swift Progression" : "Auto-advance Prompts"}
            description={enableRpgMode 
              ? "Automatically reveal the next challenge or require manual progression"
              : "Automatically show the next prompt or require manual continue"
            }
            checked={autoProceed}
            onCheckedChange={toggleAutoProceed}
            enableRpgMode={enableRpgMode}
            darkMode={darkMode}
          />
          
          <SettingsToggleItem
            title={enableRpgMode ? "Mystic Guidance" : "Untangle Mode"}
            description={enableRpgMode 
              ? "Invoke additional mystical insights when the path grows unclear"
              : "Offer extra reflection options when you're stuck"
            }
            checked={enableLensFocus}
            onCheckedChange={toggleLensFocus}
            enableRpgMode={enableRpgMode}
            darkMode={darkMode}
          />
          
          <SettingsToggleItem
            title={enableRpgMode ? "Oracle's Wisdom" : "Feedback Collection"}
            description={enableRpgMode 
              ? "Receive mystical rune ratings to enhance your journey"
              : "Get helpful emoji ratings to improve your experience"
            }
            checked={enableFeedback}
            onCheckedChange={toggleFeedback}
            enableRpgMode={enableRpgMode}
            darkMode={darkMode}
          />

          <SettingsToggleItem
            title="RPG Adventure Mode"
            description={enableRpgMode 
              ? "Immerse yourself completely in the realm of fantasy and adventure with character stats"
              : "Transform your journey into an epic fantasy adventure with character progression"
            }
            checked={enableRpgMode}
            onCheckedChange={toggleRpgMode}
            enableRpgMode={enableRpgMode}
            darkMode={darkMode}
          />

          <SettingsToggleItem
            title={enableRpgMode ? "Quest Layout Order" : "Layout Direction"}
            description={enableRpgMode 
              ? `${layoutDirection === 'ltr' ? 'Quest log on left, dice on right' : 'Dice on left, quest log on right'}`
              : `${layoutDirection === 'ltr' ? 'Journal on left, dice on right' : 'Dice on left, journal on right'}`
            }
            checked={layoutDirection === 'rtl'}
            onCheckedChange={toggleLayoutDirection}
            enableRpgMode={enableRpgMode}
            darkMode={darkMode}
            icon={<ArrowLeftRight className="h-4 w-4" />}
          />
        </div>
        
        <Button 
          onClick={handleSaveSettings}
          disabled={isSaving}
          className={cn(
            "w-full mt-6 text-white font-semibold",
            enableRpgMode ? (
              darkMode 
                ? "bg-amber-700 hover:bg-amber-600 border-amber-500"
                : "bg-amber-700 hover:bg-amber-600 border-amber-600"
            ) : (
              "bg-[#6127A0] hover:bg-[#4d1f80]"
            )
          )}
        >
          {isSaving ? (
            enableRpgMode ? "Recording in ancient tomes..." : "Saving..."
          ) : (
            enableRpgMode ? "Record Preferences" : "Save Settings"
          )}
        </Button>
      </Card>
      
      <ErrorBoundary fallback={<div>Settings footer unavailable</div>}>
        <SettingsFooter enableRpgMode={enableRpgMode} darkMode={darkMode} />
      </ErrorBoundary>
    </div>
  );
};

export default Settings;
